import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { WebActivityOutletCtx, WebActivityParticipant } from "../../webInterface";
import { useCallback, useEffect, useState } from "react";
import { getWebActivityParticipants } from "../../webBridge";
import { Table, Space, Card, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";

const WebActivityParticipants: React.FC = () => {
    const data = useOutletContext() as WebActivityOutletCtx;
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const urlCategoryId = searchParams.get('category_id');

    const requestedCategoryId = urlCategoryId ? parseInt(urlCategoryId) : 0;
    const initialCategoryId = data.categories.some(category => category.id === requestedCategoryId)
        ? requestedCategoryId
        : data.categories[0]?.id || 0;
    const [categoryId, setCategoryId] = useState<number>(initialCategoryId);
    const [participants, setParticipants] = useState<WebActivityParticipant[]>([]);
    
    const loadParticipants = useCallback(async () => {
        const res = await getWebActivityParticipants(data.activity.id, categoryId);
        if (!res.ok) {
            setParticipants([]);
            return;
        }
        const resp = await res.json() as { items?: WebActivityParticipant[] };
        setParticipants(Array.isArray(resp.items) ? resp.items : []);
    }, [data.activity.id, categoryId]);

    useEffect(() => {
        if (categoryId) {
            loadParticipants();
        } else {
            setParticipants([]);
        }
    }, [categoryId, loadParticipants]);

    const handleCategoryChange = (newCategoryId: number) => {
        setCategoryId(newCategoryId);
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('category_id', newCategoryId.toString());
        navigate(`${location.pathname}?${newSearchParams.toString()}`);
    };

    const columns: ColumnsType<WebActivityParticipant> = [
        {
            title: 'Name',
            dataIndex: 'display_name',
            key: 'display_name',
        },
        {
            title: 'First Name',
            dataIndex: 'first_name',
            key: 'first_name',
        },
        {
            title: 'Last Name',
            dataIndex: 'last_name',
            key: 'last_name',
        },
        {
            title: "Gender",
            dataIndex: "gender",
            key: "gender",
        },
        {
            title: 'License Number',
            dataIndex: 'license_number',
            key: 'license_number',
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: 'Team',
            dataIndex: 'team',
            key: 'team',
        },
    ];

    if (data.categories.length === 0) {
        return <Empty description="No categories available" />;
    }
    
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card>
                <Space wrap>
                    {data.categories.map((category) => (
                        <Card
                            key={category.id}
                            size="small"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: categoryId === category.id ? '#1890ff' : '#fff',
                                color: categoryId === category.id ? '#fff' : '#000',
                            }}
                            onClick={() => handleCategoryChange(category.id)}
                        >
                            {category.name} ({category.participant_count})
                        </Card>
                    ))}
                </Space>
            </Card>
            
            <Table
                columns={columns}
                dataSource={participants}
                rowKey="id"
                pagination={false}
            />
        </Space>
    );
};

export default WebActivityParticipants;
